import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    Alert,
    CircularProgress,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { Close, Upload as UploadIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createJobApplicant } from '../../store/slices/jobApplicantSlice';
import { useTheme } from '../../contexts/ThemeContext';

interface JobApplicantFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const JobApplicantForm: React.FC<JobApplicantFormProps> = ({ open, onClose, onSuccess }) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.jobApplicants);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        designation: '',
        experience: '',
        education: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [resumeFile, setResumeFile] = useState<File | null>(null);

    useEffect(() => {
        if (open) {
            setFormData({
                name: '',
                email: '',
                phone: '',
                designation: '',
                experience: '',
                education: ''
            });
            setResumeFile(null);
            setErrors({});
        }
    }, [open]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setErrors(prev => ({
                    ...prev,
                    resume: 'Please upload a PDF, DOC, or DOCX file'
                }));
                return;
            }
            
            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    resume: 'File size must be less than 2MB'
                }));
                return;
            }
            
            setResumeFile(file);
            setErrors(prev => ({
                ...prev,
                resume: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        }

        if (!formData.designation.trim()) {
            newErrors.designation = 'Designation is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        const submitData = {
            ...formData,
            resume: resumeFile
        };

        const result = await dispatch(createJobApplicant(submitData));
        
        if (createJobApplicant.fulfilled.match(result)) {
            onSuccess?.();
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                }
            }}
        >
            <DialogTitle
                sx={{
                    pb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    mb: 2
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Add New Job Applicant
                </Typography>
                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: theme.palette.text.secondary }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Full Name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Designation"
                            value={formData.designation}
                            onChange={(e) => handleInputChange('designation', e.target.value)}
                            error={!!errors.designation}
                            helperText={errors.designation}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Experience"
                            value={formData.experience}
                            onChange={(e) => handleInputChange('experience', e.target.value)}
                            placeholder="e.g., 3 years in software development"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Education"
                            value={formData.education}
                            onChange={(e) => handleInputChange('education', e.target.value)}
                            placeholder="e.g., Bachelor's in Computer Science"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box>
                            <input
                                accept=".pdf,.doc,.docx"
                                style={{ display: 'none' }}
                                id="resume-file"
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="resume-file">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadIcon />}
                                    sx={{
                                        borderRadius: 3,
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    Upload Resume (PDF, DOC, DOCX - Max 2MB)
                                </Button>
                            </label>
                            {resumeFile && (
                                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.success.main }}>
                                    Selected: {resumeFile.name}
                                </Typography>
                            )}
                            {errors.resume && (
                                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.error.main }}>
                                    {errors.resume}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    p: isMobile ? 2 : 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 0
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                    fullWidth={isMobile}
                    sx={{
                        borderRadius: 3,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderWidth: '1px',
                            },
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderWidth: '1px',
                            },
                        },
                    }}
                >
                    {loading ? 'Creating...' : 'Create Applicant'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobApplicantForm;
